/* Kiwi Ears Product Submenu v1.0.0 | Native anchors, no frameworks. */
(() => {
  'use strict';
  if (customElements.get('ke-product-subnav')) return;
  const DEFAULT_SECTIONS = [
    {key:'overview', label:'Overview', detail:'Product story and highlights', group:'Discover', primary:true},
    {key:'technology', label:'Technology', detail:'Drivers and construction', group:'Discover', primary:true},
    {key:'sound', label:'Sound', detail:'Tuning and frequency response', group:'Discover', primary:true},
    {key:'fit-use', label:'Fit & use', detail:'Who it is for and how it fits', group:'Discover', primary:true},
    {key:'specs', label:'Specs', detail:'Drivers, acoustics, build and cable', group:'Decide', primary:true},
    {key:'in-the-box', label:'In the box', detail:'Included items and accessories', group:'Decide', primary:false, parent:'specs'},
    {key:'compare', label:'Compare', detail:'Where this product fits', group:'Decide', primary:true},
    {key:'video', label:'Video', detail:'See it in motion', group:'Explore more', primary:false},
    {key:'reviews', label:'Reviews', detail:'Expert reviews and impressions', group:'Decide', primary:true},
    {key:'customer-reviews', label:'Customer reviews', detail:'Feedback from owners', group:'Decide', primary:false, parent:'reviews'},
    {key:'gallery', label:'Gallery', detail:'Design and detail photography', group:'Explore more', primary:false},
    {key:'pairings', label:'Pairings', detail:'Pairs well with', group:'Explore more', primary:false},
    {key:'faq', label:'FAQ', detail:'Common product questions', group:'Decide', primary:true}
  ];
  const DEFAULTS = {
    productTitle:'Product', eyebrow:'On this page',
    headerSelector:'[data-ke-site-header]', headerOffset:-1,
    purchaseSelector:'[data-ke-purchase]', purchaseLabel:'Choose options', purchaseShortLabel:'Options',
    directoryLabel:'All sections', dialogTitle:'Explore this product', closeLabel:'Close sections',
    navLabel:'Product sections', sectionGap:18, debug:false, sections:DEFAULT_SECTIONS,
    /* Kiwi Ears (Rene 2026-09-15): the bar carries the real Add-to-cart
       button. It PROXIES the buy box's own button rather than re-implementing
       cart logic, so variant id, quantity, AJAX add, drawer open and the
       no-fetch form fallback all stay in one place. */
    cartSelector:'#pk-atc', cartLabel:'Add to cart'
  };
  const glyphs = {
    directory:'<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 6h12M8 12h12M8 18h12"/><path d="M3 6h1M3 12h1M3 18h1" stroke-width="2.5" stroke-linecap="round"/></svg>',
    close:'<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" stroke-linecap="round"/></svg>',
    options:'<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 8h9M17 8h3M4 16h4M12 16h8" stroke-linecap="round"/><circle cx="15" cy="8" r="2.2"/><circle cx="10" cy="16" r="2.2"/></svg>'
  };
  const node = (tag, cls, text) => {
    const e = document.createElement(tag); if(cls) e.className = cls; if(text != null) e.textContent = text; return e;
  };
  const query = sel => { try { return sel ? document.querySelector(sel) : null; } catch (_) { return null; } };
  const visible = e => !!e && e.isConnected && !e.closest('[hidden]') && e.getClientRects().length > 0 && getComputedStyle(e).visibility !== 'hidden';
  const clamp = n => Math.min(1, Math.max(0, n));
  let uid = 0;

  class ProductSubnav extends HTMLElement {
    connectedCallback() {
      if(this._connected) return;
      this._connected = true;
      this._setupFrame = requestAnimationFrame(() => this.setup());
    }
    setup() {
      if(!this.isConnected) return;
      this.abort = new AbortController();
      const raw = this.querySelector('[data-ke-nav-config]');
      let supplied = this._supplied || {};
      try { supplied = raw ? JSON.parse(raw.textContent) : supplied; }
      catch (_) { console.warn('[Kiwi submenu] Invalid JSON; using default section markers.'); }
      this._supplied = supplied;
      this.frame = 0; this.signature = null;
      this.config = {...DEFAULTS, ...supplied};
      if(!Array.isArray(this.config.sections)) this.config.sections = DEFAULT_SECTIONS;
      this.config.sections = this.config.sections.filter(s => s && typeof s.key === 'string' && /^[a-z0-9-]+$/.test(s.key)).slice(0,30);
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.stickyHost = this.closest('.ke-pnav-shopify-section') || this;
      this.previousActive = null;
      this.manualRailUntil = 0;
      this.build();
      const on = (target, name, fn, options = {}) => target.addEventListener(name, fn, {...options, signal:this.abort.signal});
      this.on = on;
      on(this, 'click', e => this.handleClick(e));
      on(this.links, 'scroll', () => this.updateEdges(), {passive:true});
      on(this.links, 'pointerdown', () => { this.manualRailUntil = performance.now()+1800; }, {passive:true});
      on(this.links, 'wheel', () => { this.manualRailUntil = performance.now()+1800; }, {passive:true});
      on(this.links, 'focusin', e => { if(e.target.matches('.ke-pnav__link')) this.revealLink(e.target, false); });
      on(this.directoryButton, 'click', () => this.openDirectory());
      on(this.cart, 'click', () => {
        const t=this.cartTarget;
        if(t && !t.disabled) t.click();   /* the buy box owns the actual add */
      });
      on(this.closeButton, 'click', () => this.dialog.close());
      on(this.dialog, 'close', () => this.afterClose());
      on(this.dialog, 'click', e => {
        if(e.target !== this.dialog) return;
        const r = this.dialog.getBoundingClientRect();
        if(e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) this.dialog.close();
      });
      on(window, 'scroll', () => this.schedule(), {passive:true});
      on(window, 'resize', () => { this.scheduleRefresh(); this.schedule(); }, {passive:true});
      on(window, 'hashchange', () => this.followHash());
      on(window, 'popstate', () => this.followHash());
      on(window, 'pageshow', () => this.schedule());
      // Stop programmatic alignment when the visitor takes over scrolling.
      on(window, 'wheel', () => this.cancelNavigation(), {passive:true});
      on(window, 'touchstart', () => this.cancelNavigation(), {passive:true});
      on(window, 'keydown', e => {
        if(['PageUp','PageDown','ArrowUp','ArrowDown','Home','End',' '].includes(e.key)) this.cancelNavigation();
      });
      on(document, 'transitionrun', e => {
        if(this.headers?.some(h => h === e.target || h.contains(e.target))) { this.followHeaderUntil = performance.now()+650; this.schedule(); }
      }, {passive:true});
      ['shopify:section:load','shopify:section:unload','shopify:section:reorder'].forEach(name => on(document, name, () => this.scheduleRefresh()));
      this.refresh();
      this.mutations = new MutationObserver(records => {
        if(records.some(r => !this.contains(r.target))) this.scheduleRefresh();
      });
      this.mutations.observe(query('main') || document.body, {subtree:true, childList:true, attributes:true, attributeFilter:['hidden']});
      if('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => { this.schedule(); this.scheduleRefresh(); });
        this.resizeObserver.observe(this.surface);
        const main = query('main'); if(main) this.resizeObserver.observe(main);
      }
      this._hashTimer = setTimeout(() => this.followHash(), 100);
    }
    build() {
      this.replaceChildren();
      this.surface = node('div','ke-pnav__surface');
      const inner = node('div','ke-pnav__inner');
      const identity = node('div','ke-pnav__identity');
      identity.append(node('span','',this.config.eyebrow), node('strong','',this.config.productTitle));
      this.rail = node('nav','ke-pnav__rail');
      this.rail.setAttribute('aria-label',this.config.navLabel);
      this.links = node('div','ke-pnav__links'); this.rail.append(this.links);
      const actions = node('div','ke-pnav__actions');
      this.directoryButton = node('button','ke-pnav__directory ke-pnav__circle');
      this.directoryButton.type = 'button';
      this.directoryButton.innerHTML = glyphs.directory;
      this.directoryButton.append(node('span','ke-pnav__directory-label',this.config.directoryLabel));
      this.directoryButton.setAttribute('aria-label', this.config.directoryLabel);
      this.directoryButton.title = this.config.directoryLabel;
      this.directoryButton.setAttribute('aria-haspopup','dialog');
      this.directoryButton.setAttribute('aria-expanded','false');
      /* Right cluster (Rene 2026-09-15): sections circle, options circle, then
         the real Add-to-cart button. The options control keeps the original
         jump-to-purchase behaviour; only its shape changed. */
      this.buy = node('a','ke-pnav__buy ke-pnav__circle'); this.buy.setAttribute('data-ke-jump','purchase');
      this.buy.innerHTML = glyphs.options;
      this.buy.setAttribute('aria-label',this.config.purchaseLabel);
      this.buy.title = this.config.purchaseLabel;
      this.buy.append(node('span','ke-pnav__sr',this.config.purchaseLabel));
      this.cart = node('button','ke-pnav__cart'); this.cart.type='button';
      this.cart.append(node('span','ke-pnav__cart-label',this.config.cartLabel));
      actions.append(this.directoryButton,this.buy,this.cart);
      inner.append(identity,this.rail,actions);
      const track=node('div','ke-pnav__track'); track.setAttribute('aria-hidden','true');
      this.progress=node('i','ke-pnav__progress'); track.append(this.progress);
      this.surface.append(inner,track);
      const id='ke-pnav-directory-'+(++uid);
      this.dialog=node('dialog','ke-pnav__dialog'); this.dialog.id=id;
      this.dialog.setAttribute('aria-labelledby',id+'-heading');
      this.directoryButton.setAttribute('aria-controls',id);
      const head=node('div','ke-pnav__dialog-head'); const title=node('div');
      const h2=node('h2','',this.config.dialogTitle); h2.id=id+'-heading';
      title.append(node('p','',this.config.productTitle),h2);
      this.closeButton=node('button','ke-pnav__close'); this.closeButton.type='button';
      this.closeButton.innerHTML=glyphs.close; this.closeButton.setAttribute('aria-label',this.config.closeLabel);
      head.append(title,this.closeButton);
      this.directoryContent=node('nav','ke-pnav__directory-content');
      this.directoryContent.setAttribute('aria-label',this.config.directoryLabel);
      this.dialog.append(head,this.directoryContent);
      this.debugBox=node('div','ke-pnav__debug'); this.debugBox.hidden=true;
      this.append(this.surface,this.dialog,this.debugBox);
    }
    ensureID(element, key) {
      if(element.id) return element.id;
      let id='ke-product-'+key, suffix=1;
      while(document.getElementById(id)) id='ke-product-'+key+'-'+suffix++;
      element.id=id; return id;
    }
    refresh() {
      if(!this.isConnected) return;
      const visited=new Set();
      const items=[];
      this.missing=[];
      this.config.sections.forEach(config => {
        const selector=config.selector || '[data-ke-section="'+config.key+'"]';
        const el=query(selector);
        if(!visible(el) || visited.has(el)) { this.missing.push(config.key); return; }
        visited.add(el);
        items.push({...config, label:config.label || config.key, el, id:this.ensureID(el,config.key)});
      });
      // Follow actual document order, not a frozen product-specific section order.
      items.sort((a,b) => a.el.compareDocumentPosition(b.el) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1);
      this.items=items;
      this.purchase=query(this.config.purchaseSelector);
      this.cartTarget=query(this.config.cartSelector);
      this.cartObserver?.disconnect();
      if(this.cartTarget) {
        /* Mirror the buy box button: its label changes with the variant
           ("Add to Cart" / "Pre-order" / "Sold out" / "Adding…") and it
           disables itself, so watching it keeps the two in step for free. */
        this.cartObserver=new MutationObserver(()=>this.syncCart());
        this.cartObserver.observe(this.cartTarget,{attributes:true,attributeFilter:['disabled'],childList:true,characterData:true,subtree:true});
      }
      this.syncCart();
      this.buy.hidden=!visible(this.purchase);
      if(!this.buy.hidden) this.buy.href='#'+this.ensureID(this.purchase,'purchase');
      try { this.headers=[...document.querySelectorAll(this.config.headerSelector || '[data-ke-site-header]')].filter(el=> !this.contains(el)); }
      catch (_) { this.headers=[]; }
      const signature=items.map(i=>i.key+':'+i.id+':'+i.label).join('|');
      if(signature !== this.signature) {
        this.signature=signature;
        this.links.replaceChildren(); this.directoryContent.replaceChildren();
        const groups=new Map();
        items.forEach(item=> {
          if(item.primary) {
            const link=node('a','ke-pnav__link',item.label); link.href='#'+item.id;
            link.dataset.keJump=item.key; this.links.append(link);
          }
          const g=item.group || 'Sections';
          if(!groups.has(g)) {
            const section=node('div','ke-pnav__group'); const list=node('div','ke-pnav__group-links');
            section.append(node('h3','',g),list); this.directoryContent.append(section); groups.set(g,list);
          }
          const link=node('a','ke-pnav__entry'); link.href='#'+item.id; link.dataset.keJump=item.key;
          link.append(node('strong','',item.label),node('span','',item.detail || ''));
          groups.get(g).append(link);
        });
        this.previousActive=null;
      }
      this.surface.hidden=items.length===0;
      this.dataset.ready='true';
      this.debugBox.hidden=!(this.config.debug && this.missing.length);
      this.debugBox.textContent='Submenu setup: connect these section targets in the theme: '+this.missing.join(', ')+'. Unmatched sections are hidden from customers.';
      this.schedule();
    }
    headerHeight() {
      if(Number(this.config.headerOffset)>=0) return Number(this.config.headerOffset);
      let bottom=0;
      this.headers.forEach(el => {
        if(!visible(el)) return;
        let p=el, pinned=false;
        for(let n=0; p && n<5; n++, p=p.parentElement) {
          if(['fixed','sticky'].includes(getComputedStyle(p).position)) { pinned=true; break; }
        }
        const r=el.getBoundingClientRect();
        if(pinned && r.bottom>0 && r.top<Math.min(window.innerHeight/2,240)) bottom=Math.max(bottom,r.bottom);
      });
      return Math.max(0,Math.round(bottom));
    }
    scheduleRefresh() {
      clearTimeout(this.refreshTimer);
      this.refreshTimer=setTimeout(()=>this.refresh(),120);
    }
    schedule() {
      if(this.frame) return;
      this.frame=requestAnimationFrame(()=>{this.frame=0;this.tick();});
    }
    syncCart() {
      if(!this.cart) return;
      const t=this.cartTarget;
      this.cart.hidden=!visible(t);
      if(!t) return;
      const label=(t.textContent||'').trim();
      if(label) this.cart.firstElementChild.textContent=label;
      this.cart.disabled=!!t.disabled;
      this.cart.setAttribute('aria-label',label||this.config.cartLabel);
    }
    topOverlap() {
      /* Kiwi Ears (Rene 2026-09-15): the bar can be pinned to the BOTTOM of the
         viewport instead of under the header. Only a top-pinned bar hides the
         area a jumped-to section lands in, so the scroll maths has to ask where
         the bar actually is rather than always subtracting its height. */
      /* Ask the geometry, not the declaration: getComputedStyle resolves
         `top:auto` on a fixed element to a used pixel value, so the stylesheet
         cannot be read to tell top-pinned from bottom-pinned. */
      const r = this.surface.getBoundingClientRect();
      return r.top <= this.headerHeight() + 2 ? r.height : 0;
    }
    tick() {
      if(!this.items?.length || !this.isConnected) return;
      const top=this.headerHeight();
      this.style.setProperty('--ke-pnav-top',top+'px');
      this.stickyHost.style.setProperty('--ke-pnav-top',top+'px');
      const h=this.surface.getBoundingClientRect().height;
      this.height=h;
      const hostTop=this.stickyHost.getBoundingClientRect().top;
      this.dataset.stuck=String(hostTop<=top+1);
      const readingLine=top+this.topOverlap()+Number(this.config.sectionGap || 18)+2;
      const visibleItems=this.items.filter(i=>visible(i.el));
      let active=visibleItems[0];
      for(const item of visibleItems) {
        if(item.el.getBoundingClientRect().top<=readingLine) active=item;
      }
      if(this.pending) active=this.items.find(i=>i.key===this.pending.key) || active;
      if(active) this.activate(active);
      if(visibleItems.length) {
        const start=visibleItems[0].el.getBoundingClientRect().top+scrollY-readingLine;
        const last=visibleItems.at(-1).el.getBoundingClientRect();
        const end=last.bottom+scrollY-innerHeight;
        this.progress.style.transform='scaleX('+clamp((scrollY-start)/Math.max(1,end-start))+')';
      }
      this.updateEdges();
      if(this.followHeaderUntil>performance.now()) this.schedule();
    }
    activate(item) {
      if(item.key===this.previousActive) return;
      this.previousActive=item.key;
      this.dataset.active=item.key;
      const primaryKey=item.primary ? item.key : item.parent;
      this.links.querySelectorAll('a').forEach(link=> {
        const current=link.dataset.keJump===primaryKey;
        current ? link.setAttribute('aria-current','location') : link.removeAttribute('aria-current');
        if(current && performance.now()>this.manualRailUntil) this.revealLink(link,!this.motion.matches);
      });
      this.directoryContent.querySelectorAll('a').forEach(link=> {
        link.dataset.keJump===item.key ? link.setAttribute('aria-current','location') : link.removeAttribute('aria-current');
      });
      const primaryExists=!![...this.links.querySelectorAll('a')].find(a=>a.dataset.keJump===primaryKey);
      this.directoryButton.dataset.current=String(!primaryExists);
      this.directoryButton.setAttribute('aria-label',this.config.directoryLabel+'. Current section: '+item.label);
      this.dispatchEvent(new CustomEvent('ke:section-change',{bubbles:true,detail:{key:item.key,label:item.label}}));
    }
    revealLink(link,smooth) {
      const outer=this.links.getBoundingClientRect(), inner=link.getBoundingClientRect();
      if(inner.left<outer.left+18 || inner.right>outer.right-18) {
        const offset=inner.left-outer.left-(outer.width-inner.width)/2;
        this.links.scrollTo({left:this.links.scrollLeft+offset,behavior:smooth?'smooth':'instant'});
      }
    }
    updateEdges() {
      this.rail.dataset.left=String(this.links.scrollLeft>5);
      this.rail.dataset.right=String(this.links.scrollLeft+this.links.clientWidth<this.links.scrollWidth-5);
    }
    handleClick(e) {
      const link=e.target.closest('a[data-ke-jump]');
      if(!link || !this.contains(link) || e.button!==0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const key=link.dataset.keJump;
      const target=key==='purchase'?this.purchase:this.items.find(i=>i.key===key)?.el;
      if(!visible(target)) { e.preventDefault(); this.refresh(); return; }
      e.preventDefault();
      if(this.dialog.open) this.dialog.close();
      this.navigate(target,key,true,true);
    }
    navigate(target,key,push,focus) {
      this.cancelNavigation();
      const id=this.ensureID(target,key);
      if(push && location.hash!=='#'+id) {
        try { const u=new URL(location.href);u.hash=id;history.pushState(null,'',u); } catch (_) { /* hash is optional, scrolling is not */ }
      }
      const getY=()=>Math.max(0,scrollY+target.getBoundingClientRect().top-this.headerHeight()-(key==='purchase'?0:this.topOverlap())-Number(this.config.sectionGap||18));
      this.pending={key, started:performance.now(), lastY:scrollY, stable:0};
      window.scrollTo({top:getY(),behavior:this.motion.matches?'instant':'smooth'});
      if(focus) {
        const heading=target.matches('h1,h2,h3')?target:target.querySelector('h1,h2,h3,[data-ke-focus]') || target;
        const added=!heading.hasAttribute('tabindex'); if(added) heading.setAttribute('tabindex','-1');
        heading.focus({preventScroll:true});
        if(added) heading.addEventListener('blur',()=>heading.removeAttribute('tabindex'),{once:true});
      }
      const settle=()=>{
        const p=this.pending; if(!p || !this.isConnected) return;
        p.stable=Math.abs(scrollY-p.lastY)<.5?p.stable+1:0; p.lastY=scrollY;
        if((p.stable>8 && performance.now()-p.started>160) || performance.now()-p.started>1700) {
          // One final alignment if the theme header changed during smooth scrolling.
          const wanted=getY(); if(Math.abs(wanted-scrollY)>2) window.scrollTo({top:wanted,behavior:'instant'});
          this.pending=null;this.schedule();this.navFrame=0;
        } else this.navFrame=requestAnimationFrame(settle);
      };
      this.navFrame=requestAnimationFrame(settle); this.schedule();
    }
    cancelNavigation() { this.pending=null; if(this.navFrame) cancelAnimationFrame(this.navFrame);this.navFrame=0; }
    followHash() {
      if(!this.items?.length || !location.hash) return;
      let id;try{id=decodeURIComponent(location.hash.slice(1));}catch(_){return;}
      const match=this.items.find(i=>i.id===id);
      if(match) this.navigate(match.el,match.key,false,false);
    }
    openDirectory() {
      if(this.dialog.open || !this.items.length) return;
      this.cancelNavigation();
      this.bodyStyle={overflow:document.body.style.overflow,paddingRight:document.body.style.paddingRight};
      const gap=innerWidth-document.documentElement.clientWidth;
      if(gap>0) document.body.style.paddingRight=(parseFloat(getComputedStyle(document.body).paddingRight)+gap)+'px';
      document.body.style.overflow='hidden';
      this.dialog.showModal(); this.directoryButton.setAttribute('aria-expanded','true');
      const current=this.directoryContent.querySelector('[aria-current]');
      (current || this.closeButton).focus({preventScroll:true});
      if(current) current.scrollIntoView({block:'nearest',behavior:'instant'});
    }
    afterClose() {
      if(this.bodyStyle) {
        document.body.style.overflow=this.bodyStyle.overflow;
        document.body.style.paddingRight=this.bodyStyle.paddingRight;
        this.bodyStyle=null;
      }
      this.directoryButton?.setAttribute('aria-expanded','false');
      // Native <dialog> restores focus. Explicit section jumps then focus the destination.
    }
    getDiagnostics() {
      return {matched:this.items.map(i=>({key:i.key,id:i.id,primary:!!i.primary})),missing:[...this.missing],headerOffset:this.headerHeight(),active:this.dataset.active,purchaseFound:visible(this.purchase)};
    }
    disconnectedCallback() {
      this._connected=false; this.abort?.abort();this.mutations?.disconnect();this.resizeObserver?.disconnect();this.cartObserver?.disconnect();
      cancelAnimationFrame(this._setupFrame);cancelAnimationFrame(this.frame);this.cancelNavigation();
      clearTimeout(this.refreshTimer);clearTimeout(this._hashTimer);
      this.afterClose();
    }
  }
  customElements.define('ke-product-subnav',ProductSubnav);
})();
